import type { PersonId, SettlementTransfer } from './types.ts';

/**
 * Turns net balances into as few transfers as the greedy rule gives: the largest debtor pays
 * the largest creditor, repeat. Chains like A→B→C never appear because balances are netted
 * first. At most n−1 transfers; deterministic (ties by the given person order).
 * Balances must sum to zero.
 */
export function simplifyDebts(
  balances: readonly { personId: PersonId; netMinor: bigint }[],
  order: ReadonlyMap<PersonId, number>,
): SettlementTransfer[] {
  const total = balances.reduce((acc, b) => acc + b.netMinor, 0n);
  if (total !== 0n) throw new RangeError(`Balances must sum to zero, got ${total}`);

  const rank = (id: PersonId) => order.get(id) ?? Number.MAX_SAFE_INTEGER;
  const byAmountThenOrder = (a: { personId: PersonId; amount: bigint }, b: { personId: PersonId; amount: bigint }) =>
    a.amount !== b.amount ? (a.amount > b.amount ? -1 : 1) : rank(a.personId) - rank(b.personId);

  const creditors = balances
    .filter((b) => b.netMinor > 0n)
    .map((b) => ({ personId: b.personId, amount: b.netMinor }));
  const debtors = balances
    .filter((b) => b.netMinor < 0n)
    .map((b) => ({ personId: b.personId, amount: -b.netMinor }));

  const transfers: SettlementTransfer[] = [];
  while (creditors.length > 0 && debtors.length > 0) {
    creditors.sort(byAmountThenOrder);
    debtors.sort(byAmountThenOrder);
    const creditor = creditors[0]!;
    const debtor = debtors[0]!;
    const amount = creditor.amount < debtor.amount ? creditor.amount : debtor.amount;
    transfers.push({ fromPersonId: debtor.personId, toPersonId: creditor.personId, amountMinor: amount });
    creditor.amount -= amount;
    debtor.amount -= amount;
    if (creditor.amount === 0n) creditors.shift();
    if (debtor.amount === 0n) debtors.shift();
  }
  return transfers;
}
